import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';

interface DocxPreviewFrameProps {
    /** 原件的预签名 URL（inline）。取字节走 fetch，需存储桶 CORS 放行 GET */
    url: string;
    /** 取不到字节或渲染失败时的降级呈现，由调用方提供（通常是 Alert + 下载按钮） */
    renderError: React.ReactNode;
    /** 与 PDF 预览 iframe 保持一致的高度 */
    height?: number;
}

type Phase = 'loading' | 'ready' | 'error';

/** 与 docx-preview 生成的 CSS 选择器前缀对应 */
const DOCX_CLASS = 'docx';

/**
 * 把 docx-preview 的产物包成一份完整文档，供沙箱 iframe 的 srcdoc 使用。
 *
 * 传入的 html 已含 docx-preview 自己生成的 `<style>`，此处只补页面级底色与居中，
 * 不引入任何脚本 —— iframe 未开 allow-scripts，脚本本就不会执行。
 */
const buildSrcDoc = (html: string): string =>
    '<!doctype html><html><head><meta charset="utf-8">'
    + '<style>'
    + 'html,body{margin:0;padding:0;background:#f5f5f5;}'
    + `.${DOCX_CLASS}-wrapper{padding:16px 0;}`
    + '</style></head><body>'
    + html
    + '</body></html>';

/**
 * DSP 自制 docx Invoice 的页面内预览。
 *
 * 渲染链路：fetch 原件字节 → 动态载入 docx-preview → 渲染到游离节点 →
 * 整体搬进 `sandbox=""` 的 iframe。
 *
 * 之所以不直接渲染进当前页面 DOM：docx 是 DSP 上传的不可信内容，而 docx-preview
 * 会把文档派生的 CSS 注入一个 `<style>` 元素，作用域仅靠其自身的类名前缀。
 * 「确认 Invoice」这个资金操作按钮就在同一个页面上，恶意 docx 用 CSS 遮罩做点击劫持
 * 是现实场景。放进不给 allow-scripts、不给 allow-same-origin 的 iframe 后，
 * 脚本不执行、CSS 出不了这个文档、也拿不到父页面与 Cookie，整类风险一次性关闭。
 */
const DocxPreviewFrame: React.FC<DocxPreviewFrameProps> = ({ url, renderError, height = 720 }) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState<Phase>('loading');
    const [srcDoc, setSrcDoc] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;

        const run = async () => {
            setPhase('loading');
            setSrcDoc('');
            try {
                const resp = await fetch(url, { signal: controller.signal });
                if (!resp.ok) {
                    throw new Error(`fetch docx failed: ${resp.status}`);
                }
                const blob = await resp.blob();
                // 动态引入：docx-preview 体积约 1MB，不能进主 bundle
                const docx = await import('docx-preview');
                if (cancelled) {
                    return;
                }

                const holder = document.createElement('div');
                // styleContainer 省略即与 bodyContainer 同一节点，style 与正文一起产出
                await docx.renderAsync(blob, holder, undefined, {
                    className: DOCX_CLASS,
                    inWrapper: true,
                    breakPages: true,
                    renderHeaders: true,
                    renderFooters: true,
                    renderFootnotes: true,
                    // 图片必须转 base64：沙箱 iframe 是不透明源，读不到父页面创建的 blob: URL，
                    // 用默认值会导致 logo 等内嵌图片全部裂开
                    useBase64URL: true,
                    // 不可信输入，能关的处理面一律关闭
                    experimental: false,
                    renderChanges: false,
                    renderComments: false,
                    renderAltChunks: false,
                });
                if (cancelled) {
                    return;
                }
                setSrcDoc(buildSrcDoc(holder.innerHTML));
                setPhase('ready');
            } catch (e) {
                if (cancelled || controller.signal.aborted) {
                    return;
                }
                // 预览失败不阻断开票流程，交由调用方降级呈现，不额外弹错误提示
                console.warn('[DocxPreviewFrame] render failed', e);
                setPhase('error');
            }
        };

        run();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [url]);

    if (phase === 'error') {
        return <>{renderError}</>;
    }

    if (phase === 'loading') {
        return (
            <div
                className="flex w-full flex-col items-center justify-center gap-3 border border-gray-200 bg-gray-50"
                style={{ height }}
            >
                {/* tip 不挂在 Spin 上：antd 5 的 tip 仅在 nest / fullscreen 模式下生效，否则会有告警 */}
                <Spin />
                <span className="text-gray-500">{t('dspInvoice.messages.docxLoading')}</span>
            </div>
        );
    }

    return (
        <iframe
            title={t('dspInvoice.title') as string}
            srcDoc={srcDoc}
            sandbox=""
            className="w-full border border-gray-200"
            style={{ height }}
        />
    );
};

export default DocxPreviewFrame;
